import React, { useLayoutEffect, useMemo, useState } from "react";
import { Box, Stack, useTheme, styled, Button, Grid, Paper, Typography } from "@mui/material";
import * as SC from "SC";
import { useDataGrid } from "hooks/useDataGrid";
import { useAppSelector } from "store/hooks";
import { DataGrid } from "UI/DataGrid";
import { customColors } from "models/customColors";
import { convertToReadable, unixTimeToUSTime } from "utils";
import { getActiveRides, getRidesFromDateRange } from "api/rideApi";
import { RideDetails } from "components/RideDetails";
import { useClientList } from "hooks/useClientList";
import { TextFieldWithEllipsis } from "components/TextFiledWithEllipsis";
import { CloseWindowButton } from "UI/CloseWindowButton";
import { Content, Status } from "pages/BookingsPage.style";
import { ActiveRidesMap } from "UI/ActiveRidesMap";
import { RideInfoField } from "components/RideInfoField";
import { getPollingDataByRole } from "api/userApi";
import { useApiRequest } from "api/useApiRequest";
import { useNavigate } from "react-router-dom";
import { OnboardingPipelineWidget } from "components/OnboardingPipelineWidget";

const namesSx = {
  fontFamily: "Inter",
  fontSize: "16px",
  fontWeight: 500,
  lineHeight: "18px",
  letterSpacing: "0px",
  textAlign: "left",
};

const TableLabel = styled(Box)`
  margin-bottom: 1rem;
  font-family: Inter;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0em;
  text-align: left;
`;

const MissionCard = ({ title, value }) => (
  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e7e7e7" }}>
    <Typography variant="caption" color="text.secondary">{title}</Typography>
    <Typography variant="h6">{value}</Typography>
  </Paper>
);

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "0%");

export const DashboardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { callApiRequest } = useApiRequest();
  const { getClientLabelById } = useClientList();

  const userId = useAppSelector((state) => state.user.id);
  const userRole = useAppSelector((state) => state.user.role);

  const [selectedId, setSelectedId] = useState("");
  const [reloadSignal, setReloadSignal] = useState(false);
  const [missionRides, setMissionRides] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const sendRedrawMapSignal = () =>
    setReloadSignal((prevSignal) => !prevSignal);

  const columns = [
    {
      label: "Client",
      field: "companyName",
      xs: 8,
      sm: 8,
      md: 8,
      sortableName: "COMPANY_NAME",
      renderer: (row) => <RideInfoField {...row} />,
    },
    {
      label: "Driver",
      field: "driverName",
      xs: 4,
      sm: 4,
      md: 4,
      sortableName: "DRIVER_NAME",
      renderer: (row) => (
        <Stack gap={0.75}>
          <TextFieldWithEllipsis sx={namesSx}>
            {row?.driverName ?? "Unassigned"}
          </TextFieldWithEllipsis>
          <Status
            // @ts-ignore
            row={row}
            status={row?.status}
          >
            {convertToReadable(row?.status ?? "")}
          </Status>
        </Stack>
      ),
    },
  ];

  const dataGrid = useDataGrid({
    apiHandler: getActiveRides,
    defaultSorter: {
      name: "COMPANY_NAME",
      order: "ASC",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userId}-active-bookings`,
    reloadIntervalInSeconds: 10,
  });

  const handleRowClick = (row) => {
    if (selectedId === row?.id) {
      setSelectedId("");
      return;
    }

    setSelectedId(row?.id ?? "");
  };

  const markers =
    // @ts-ignore
    dataGrid?.rows
      ?.filter(
        ({ lastKnownPositionLat, lastKnownPositionLon }) =>
          lastKnownPositionLat && lastKnownPositionLon
      )
      ?.map(
        ({
          id,
          passengerName,
          driverName,
          lastKnownPositionLat,
          lastKnownPositionLon,
          lastKnownPositionMs,
          driverWorkStatus,
        }) => ({
          id,
          title: passengerName,
          subtitle:
            !!driverName &&
            `driver: ${driverName} ${
              driverWorkStatus === "Off_Shift" && lastKnownPositionMs > 0
                ? unixTimeToUSTime(lastKnownPositionMs)
                : ""
            }`,
          driverWorkStatus,
          coordinates: {
            lat: lastKnownPositionLat,
            lon: lastKnownPositionLon,
          },
        })
      ) ?? [];

  const missionStats = useMemo(() => {
    const total = missionRides.length;
    const byStatus = missionRides.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});

    const completed = byStatus.COMPLETED || 0;
    const cancelled = byStatus.CANCELLED || 0;
    const assigned =
      (byStatus.CONFIRMATION || 0) +
      (byStatus.SCHEDULED || 0) +
      (byStatus.EN_ROUTE || 0) +
      (byStatus.ARRIVED || 0) +
      (byStatus.ON_BOARD || 0) +
      completed;

    const actionQueue = missionRides
      .filter((r) => ["UNMATCHED", "NEW", "CONFIRMATION"].includes(r.status))
      .slice(0, 5);

    return {
      total,
      completionRate: pct(completed, total),
      assignmentRate: pct(assigned, total),
      cancellationRate: pct(cancelled, total),
      actionQueue,
    };
  }, [missionRides]);

  useLayoutEffect(() => {
    // @ts-ignore
    if (dataGrid?.rows?.length) {
      sendRedrawMapSignal();
    }
    // @ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataGrid?.rows?.length]);

  useLayoutEffect(() => {
    const now = Date.now();
    const dayTo = now;
    const dayFrom = now - 30 * 24 * 60 * 60 * 1000;

    callApiRequest({
      apiRequest: getRidesFromDateRange,
      params: { dayFrom, dayTo },
      skipSpinner: true,
      skipErrorHandling: true,
      onSuccess: (data) => setMissionRides(data || []),
      onError: () => setMissionRides([]),
    });

    if (userRole) {
      callApiRequest({
        apiRequest: getPollingDataByRole,
        params: userRole,
        skipSpinner: true,
        skipErrorHandling: true,
        onSuccess: (data) => setIndicators(data || null),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  return (
    <SC.Container>
      <Stack gap={2} height={"100%"}>
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #e7e7e7" }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Mission Control</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={2}><MissionCard title="Trips (30d)" value={missionStats.total} /></Grid>
            <Grid item xs={6} md={2}><MissionCard title="Active Now" value={dataGrid?.rows?.length || 0} /></Grid>
            <Grid item xs={6} md={2}><MissionCard title="Assignment" value={missionStats.assignmentRate} /></Grid>
            <Grid item xs={6} md={2}><MissionCard title="Completion" value={missionStats.completionRate} /></Grid>
            <Grid item xs={6} md={2}><MissionCard title="Cancellation" value={missionStats.cancellationRate} /></Grid>
            <Grid item xs={6} md={2}><MissionCard title="Unmatched" value={indicators?.unmatchedRides?.length || 0} /></Grid>
          </Grid>
          {!!missionStats.actionQueue?.length && (
            <Stack sx={{ mt: 1.5 }} gap={0.5}>
              <Typography variant="subtitle2">Action Queue</Typography>
              {missionStats.actionQueue.map((r) => (
                <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption">{r.passengerName || "Unknown"} · {r.companyName || "Unknown"}</Typography>
                  <Button size="small" onClick={() => navigate(`/ride_chat/${r.id}`)}>{r.status}</Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>

        <OnboardingPipelineWidget />

        <Stack direction={"row"} height={"100%"} position={"relative"}>
          <Content>
          <SC.HalfScreenPanel>
            <TableLabel>Real-Time Rides</TableLabel>
            <DataGrid
              columns={columns}
              handleRowClick={handleRowClick}
              {...dataGrid}
              actionsAfter={
                <Button variant="outlined" color="primary" disabled>
                  All Regions
                </Button>
              }
              keyName="id"
              selectedId={selectedId}
            />
          </SC.HalfScreenPanel>
          {selectedId ? (
            <SC.HalfScreenPanel>
              <CloseWindowButton
                top={-35}
                right={-35}
                closeHandler={() => setSelectedId("")}
              />
              <RideDetails
                id={selectedId}
                getClientLabelById={getClientLabelById}
                refreshList={dataGrid.refreshData}
              />
            </SC.HalfScreenPanel>
          ) : (
            <SC.HalfScreenPanel
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: customColors[theme.palette.mode].strokeColor,
              }}
            >
              <ActiveRidesMap
                setSelectedId={setSelectedId}
                markers={markers}
                reloadSignal={reloadSignal}
              />
            </SC.HalfScreenPanel>
          )}
          </Content>
        </Stack>
      </Stack>
    </SC.Container>
  );
};
